import { z } from "zod";

const id = z.string().uuid();
const version = z.number().int().min(0).max(2_147_483_647);
const revision = z.number().int().min(1).max(2_147_483_647);
const timestamp = z.string().min(1);
const key = z.string().trim().regex(/^[a-z][a-z0-9_]{1,31}$/, "使用小写字母、数字和下划线，且以字母开头");
const credentialRef = z.string().trim().max(200).refine(value => value === "" || /^(env|secret):\/\/[A-Z][A-Z0-9_]{2,127}$|^stored:\/\/sms\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\/(secretId|secretKey)$/.test(value), "只能填写 env://、secret:// 或后台托管的短信凭据引用");
export const smsCredentialValuesSchema = z.object({
  secretId: z.string().trim().max(128).optional(),
  secretKey: z.string().trim().max(256).optional(),
}).strict();
export type SmsCredentialValues = z.infer<typeof smsCredentialValuesSchema>;

export const smsProviderSchema = z.literal("tencent_cloud");
export type SmsProvider = z.infer<typeof smsProviderSchema>;
export const smsPurposeSchema = z.enum(["login_verification", "security_notification", "business_notification", "marketing"]);
export type SmsPurpose = z.infer<typeof smsPurposeSchema>;

export const smsSignatureSchema = z.object({
  code: key,
  name: z.string().trim().min(1).max(64),
  providerSignatureId: z.string().trim().max(80),
  enabled: z.boolean(),
}).strict();
export type SmsSignature = z.infer<typeof smsSignatureSchema>;

export const smsTemplateSchema = z.object({
  code: key,
  name: z.string().trim().min(1).max(64),
  providerTemplateId: z.string().trim().max(80),
  purpose: smsPurposeSchema,
  signatureCode: key,
  contentPreview: z.string().trim().max(300),
  enabled: z.boolean(),
}).strict();
export type SmsTemplate = z.infer<typeof smsTemplateSchema>;

export const smsConfigurationValuesSchema = z.object({
  region: z.string().trim().min(1).max(64),
  sdkAppId: z.string().trim().max(80),
  secretIdRef: credentialRef,
  secretKeyRef: credentialRef,
  maxDailyMessages: z.number().int().min(0).max(1_000_000),
  perPhonePerHour: z.number().int().min(1).max(1_000),
  perIpPerHour: z.number().int().min(1).max(10_000),
  signatures: z.array(smsSignatureSchema).max(50).refine(items => new Set(items.map(item => item.code)).size === items.length, "签名标识不能重复"),
  templates: z.array(smsTemplateSchema).max(200).refine(items => new Set(items.map(item => item.code)).size === items.length, "模板标识不能重复"),
}).strict();
export type SmsConfigurationValues = z.infer<typeof smsConfigurationValuesSchema>;

export const defaultSmsConfigurationValues: SmsConfigurationValues = {
  region: "ap-guangzhou", sdkAppId: "", secretIdRef: "", secretKeyRef: "",
  maxDailyMessages: 0, perPhonePerHour: 5, perIpPerHour: 20, signatures: [], templates: [],
};

export const smsConfigurationRevisionSchema = z.object({
  revision,
  values: smsConfigurationValuesSchema,
  publishedAt: timestamp,
  publishedBy: z.string().min(1),
  action: z.enum(["publish", "rollback"]),
  sourceRevision: revision.nullable(),
}).strict();
export type SmsConfigurationRevision = z.infer<typeof smsConfigurationRevisionSchema>;

export const smsConfigurationSchema = z.object({
  id, name: z.string().trim().min(1).max(64), description: z.string().trim().max(200),
  status: z.enum(["active", "disabled"]), version,
  draft: smsConfigurationValuesSchema, published: smsConfigurationRevisionSchema.nullable(),
  bindingCount: z.number().int().min(0), createdAt: timestamp, updatedAt: timestamp, updatedBy: z.string(),
}).strict();
export type SmsConfiguration = z.infer<typeof smsConfigurationSchema>;

export const smsConfigurationListQuerySchema = z.object({
  keyword: z.string().trim().max(80).default(""), status: z.enum(["all", "active", "disabled"]).default("all"),
  page: z.coerce.number().int().min(1).max(100_000).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(20),
}).strict();
export const smsConfigurationPageSchema = z.object({ items: z.array(smsConfigurationSchema), total: z.number().int().min(0) }).strict();
export type SmsConfigurationPage = z.infer<typeof smsConfigurationPageSchema>;

export const smsConfigurationCreateSchema = z.object({
  name: z.string().trim().min(1).max(64), description: z.string().trim().max(200).default(""),
  values: smsConfigurationValuesSchema.default(defaultSmsConfigurationValues),
  credentialValues: smsCredentialValuesSchema.optional(),
}).strict();
export const smsConfigurationUpdateSchema = z.object({
  name: z.string().trim().min(1).max(64), description: z.string().trim().max(200), values: smsConfigurationValuesSchema, credentialValues: smsCredentialValuesSchema.optional(), expectedVersion: version,
}).strict();
export const smsConfigurationPublishSchema = z.object({ expectedVersion: version, mutationId: z.uuid() }).strict();
export const smsConfigurationRollbackSchema = smsConfigurationPublishSchema.extend({ targetRevision: revision }).strict();
export const smsConfigurationStatusSchema = z.object({ status: z.enum(["active", "disabled"]), expectedVersion: version }).strict();
export const smsPaginationSchema = z.object({ page: z.coerce.number().int().min(1).max(100_000).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(20) }).strict();
export const smsConfigurationRevisionPageSchema = z.object({ items: z.array(smsConfigurationRevisionSchema), total: z.number().int().min(0) }).strict();

export const smsBindingSchema = z.object({
  appId: z.string().trim().min(1).max(128), appName: z.string(), appStatus: z.enum(["active", "disabled"]),
  configurationId: id.nullable(), configurationName: z.string().nullable(), revision: revision.nullable(), version,
  updatedAt: timestamp.nullable(), updatedBy: z.string().nullable(),
}).strict();
export type SmsBinding = z.infer<typeof smsBindingSchema>;
export const smsBindingUpdateSchema = z.object({ configurationId: id.nullable(), revision: revision.nullable(), expectedVersion: version }).strict()
  .superRefine((value, context) => {
    if ((value.configurationId === null) !== (value.revision === null)) context.addIssue({ code: "custom", message: "配置和版本必须同时选择或同时清空" });
  });

export const smsDeliveryStatusSchema = z.enum(["queued", "processing", "unknown", "accepted", "delivered", "failed", "cancelled"]);
export const smsDeliveryRecordSchema = z.object({
  id, appId: z.string().min(1), configurationId: id, revision, templateCode: key, purpose: smsPurposeSchema,
  phoneMasked: z.string().regex(/^\*{3}\d{4}$/), status: smsDeliveryStatusSchema,
  providerMessageId: z.string().nullable(), errorCode: z.string().nullable(), createdAt: timestamp, updatedAt: timestamp,
  attempt: z.number().int().min(0).default(0), maxAttempts: z.number().int().positive().default(3), nextAttemptAt: timestamp.nullable().default(null),
}).strict();
export type SmsDeliveryRecord = z.infer<typeof smsDeliveryRecordSchema>;
export const smsDeliveryQuerySchema = smsPaginationSchema.extend({
  appId: z.string().trim().max(128).default(""), configurationId: z.union([id, z.literal("")]).default(""),
  status: z.union([smsDeliveryStatusSchema, z.literal("all")]).default("all"),
}).strict();
export const smsDeliveryPageSchema = z.object({ items: z.array(smsDeliveryRecordSchema), total: z.number().int().min(0) }).strict();

export const smsConfigurationAuditSchema = z.object({
  id, actorId: z.string().min(1), action: z.enum(["create", "update", "publish", "rollback", "status", "bind", "unbind"]),
  configurationId: id.nullable(), appId: z.string().nullable(), version, revision: revision.nullable(), requestId: z.string().nullable(), createdAt: timestamp,
}).strict();
export const smsConfigurationAuditPageSchema = z.object({ items: z.array(smsConfigurationAuditSchema), total: z.number().int().min(0) }).strict();

export type SmsConfigurationCreateInput = z.infer<typeof smsConfigurationCreateSchema>;
export type SmsConfigurationUpdateInput = z.infer<typeof smsConfigurationUpdateSchema>;
export type SmsConfigurationPublishInput = z.infer<typeof smsConfigurationPublishSchema>;
export type SmsConfigurationRollbackInput = z.infer<typeof smsConfigurationRollbackSchema>;
export type SmsConfigurationStatusInput = z.infer<typeof smsConfigurationStatusSchema>;
export type SmsBindingUpdateInput = z.infer<typeof smsBindingUpdateSchema>;
export type SmsConfigurationListQuery = z.infer<typeof smsConfigurationListQuerySchema>;
export type SmsPagination = z.infer<typeof smsPaginationSchema>;
export type SmsDeliveryQuery = z.infer<typeof smsDeliveryQuerySchema>;

const smsRuntimeRecipientSchema = z.object({ appId: z.string().trim().min(1).max(128), userId: z.string().trim().min(1).max(128) });
export const smsTaskCreateSchema = smsRuntimeRecipientSchema.extend({ templateCode: key, purpose: z.enum(["business_notification", "security_notification"]), parameters: z.array(z.string().max(500)).max(20), mutationId: id }).strict();
export const smsChallengeCreateSchema = smsRuntimeRecipientSchema.extend({ templateCode: key, mutationId: id }).strict();
export const smsChallengeVerifySchema = smsRuntimeRecipientSchema.extend({ code: z.string().regex(/^\d{6}$/) }).strict();
export const smsTaskAcceptedSchema = z.object({ deliveryId: id, status: z.literal("queued"), challengeId: id.nullable() }).strict();
export const smsChallengeResultSchema = z.object({ verified: z.boolean(), reason: z.string().max(80).optional() }).strict();
export const smsRuntimeStatusSchema = z.object({ enabled: z.boolean(), sendingImplemented: z.literal(true), realDeliveryVerified: z.literal(false), callbackMode: z.literal("disabled"), verificationTtlSeconds: z.literal(300), verificationMaxAttempts: z.number().int().positive() }).strict();
export const smsRuntimeControlInputSchema = z.object({ scope: z.string().trim().min(1).max(128), stopped: z.boolean(), expectedVersion: version }).strict();
export const smsRuntimeControlSchema = z.object({ scope: z.string(), stopped: z.boolean(), version }).strict();
export type SmsTaskCreateInput = z.infer<typeof smsTaskCreateSchema>;
export type SmsChallengeCreateInput = z.infer<typeof smsChallengeCreateSchema>;
export type SmsChallengeVerifyInput = z.infer<typeof smsChallengeVerifySchema>;
export type SmsRuntimeStatus = z.infer<typeof smsRuntimeStatusSchema>;
export type SmsRuntimeControlInput = z.infer<typeof smsRuntimeControlInputSchema>;
export type SmsRuntimeControl = z.infer<typeof smsRuntimeControlSchema>;
export const smsRuntimeSummarySchema = z.object({
  serverDate: z.string(), freshAt: z.string(),
  queued: z.number().int().min(0), processing: z.number().int().min(0), accepted: z.number().int().min(0), delivered: z.number().int().min(0), failed: z.number().int().min(0), unknown: z.number().int().min(0),
  rateLimited: z.number().int().min(0).nullable(),
  budget: z.object({ available: z.boolean(), reason: z.string().nullable(), budgetLimit: z.number().min(0).nullable(), reserved: z.number().min(0), acceptedUsage: z.number().min(0) }).strict(),
  recipientEndpointCount: z.number().int().min(0), recipientVaultReady: z.boolean(),
}).strict();
export type SmsRuntimeSummary = z.infer<typeof smsRuntimeSummarySchema>;

export const smsReadinessSchema = z.object({
  ready: z.boolean(), authorized: z.boolean(), checkedAt: z.string(), callbackVerified: z.literal(false),
  checks: z.array(z.object({ key: z.string(), label: z.string(), status: z.enum(["ready", "blocked", "unverified"]), reason: z.string(), remediation: z.string() }).strict()),
  nativeCallback: z.object({ documentationStatus: z.literal("documentation_unverified"), publicEndpoint: z.literal(false), reason: z.string() }).strict(),
  singleTest: z.object({ allowed: z.boolean(), reason: z.string(), maxProviderCalls: z.literal(1) }).strict(),
}).strict();
export type SmsReadiness = z.infer<typeof smsReadinessSchema>;
export const smsCanaryGateSchema = z.object({ enabled: z.boolean(), version }).strict();
export const smsCanaryGateUpdateSchema = z.object({ enabled: z.boolean(), expectedVersion: version }).strict();
export const smsCanaryAllowlistSchema = z.object({ appId: z.string(), userId: z.string(), enabled: z.boolean(), version, endpointVersion: version, phoneMasked: z.string().regex(/^\*{3}\d{4}$/) }).strict();
export const smsCanaryAllowlistUpdateSchema = smsRuntimeRecipientSchema.extend({ enabled: z.boolean(), expectedVersion: version }).strict();
const smsCanaryReviewReason = z.string().trim().min(1).max(500);
const smsCanaryMaxCostFen = z.number().int().min(1).max(10_000);
export const smsCanaryGrantSchema = z.object({ grantId: id, appId: z.string(), userId: z.string(), templateCode: key, purpose: smsPurposeSchema, status: z.enum(["authorized", "consumed", "revoked", "expired"]), version, expiresAt: timestamp, maxProviderCalls: z.literal(1), providerClaims: z.number().int().min(0).max(1), deliveryId: id.nullable(), createdAt: timestamp, reviewReason: smsCanaryReviewReason, maxCostFen: smsCanaryMaxCostFen }).strict();
export const smsCanaryAuthorizeSchema = smsRuntimeRecipientSchema.extend({ templateCode: key, purpose: z.enum(["security_notification", "business_notification"]), expiresAt: z.iso.datetime(), expectedGateVersion: version, expectedAllowlistVersion: version, mutationId: id, reviewReason: smsCanaryReviewReason, maxCostFen: smsCanaryMaxCostFen }).strict();
export const smsCanaryRevokeSchema = z.object({ grantId: id, expectedVersion: version }).strict();
export const smsCanaryExecuteSchema = smsTaskCreateSchema.extend({ grantId: id }).strict();
export type SmsCanaryGrant = z.infer<typeof smsCanaryGrantSchema>;
export type SmsCanaryExecuteInput = z.infer<typeof smsCanaryExecuteSchema>;
